import * as React from 'react';
import styled from 'styled-components';
import cl from 'classnames';

import { innerBorder, scrollBarStylesGeneral } from '@doltech/utils/lib/css-style';
import { IconSystemV2ChevronDropdownDown } from '@doltech/icons/SystemV2/IconSystemV2ChevronDropdownDown';
import { colorsV2 } from '../../../colors-v2';
import { ResponsiveTypography } from '../../../Typography/v3/ResponsiveTypography';
import { DolPopover } from '../../../Popover/DolPopover';
import { SelectOptionList } from '../../../Select/SelectOptionList/SelectOptionList';
import { DropdownItemItemLayout } from '../../../Select/DropdownItemItemLayout/DropdownItemItemLayout';
import { SelectDropdownItemContent } from '../../../Select/SelectDropdownItemContent/SelectDropdownItemContent';

const LINE_SIZE = 1;

const ScrollContent = styled.div`
  display: flex;
  flex-direction: column;

  min-width: 160px;
  max-height: 240px;
  overflow-y: auto;
  ${scrollBarStylesGeneral()}
`;

const DropdownLayout = styled.div`
  display: inline-block;
`;

const Dropdown = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  min-width: 62px;
  height: 32px;
  cursor: pointer;
`;

const Container = styled.div`
  color: ${colorsV2.black100};
  border-radius: 4px;

  .dropdown-icon {
    color: ${colorsV2.black100};
  }

  .text-line {
    bottom: ${LINE_SIZE}px;
    ${innerBorder(`${LINE_SIZE}px solid ${colorsV2.gray40}`, 'bottom')}
  }

  &:hover:not(.active):not(.readonly) {
    background-color: ${colorsV2.gray10};

    .text-line {
      ${innerBorder(`${LINE_SIZE}px solid ${colorsV2.gray80}`, 'bottom')}
    }
  }

  &.filled {
    color: ${colorsV2.blue100};

    .dropdown-action {
      min-width: auto;
    }
  }

  &.active {
    color: ${colorsV2.blue100};

    .dropdown-icon {
      color: ${colorsV2.blue100};
      transform: rotate(180deg);
    }

    .text-line {
      ${innerBorder(`${LINE_SIZE}px solid ${colorsV2.blue100}`, 'bottom')}
    }
  }

  &.correct {
    color: ${colorsV2.green100};

    .text-line {
      ${innerBorder(`${LINE_SIZE}px solid ${colorsV2.green100}`, 'bottom')}
    }
  }

  &.incorrect {
    color: ${colorsV2.red100};

    .text-line {
      ${innerBorder(`${LINE_SIZE}px solid ${colorsV2.red100}`, 'bottom')}
    }
  }

  &.readonly {
    .dropdown-icon {
      display: none;
    }

    .dropdown-action {
      min-width: auto;
    }
  }
`;

interface ExerciseBlockFieldItemDropdownProps {
  className?: string;
  mode?: 'popover' | 'action';
  status?: 'default' | 'correct' | 'incorrect';
  value?: string;
  options?: {
    key?: string;
    value?: string;
  }[];
  onClick?: () => void;
  onChange?: (changedValue: string) => void;
  readonly?: boolean;
}

export const ExerciseBlockFieldItemDropdown = (props: ExerciseBlockFieldItemDropdownProps) => {
  const { className, mode = 'popover', status = 'default', value: selectedKey, options, onClick, onChange, readonly = false } = props;
  const [visible, setVisible] = React.useState(false);
  const filled = Boolean(selectedKey);

  const selectedOption = React.useMemo(() => {
    return options?.find((option) => option.key === selectedKey);
  }, [options, selectedKey]);

  return (
    <Container
      className={cl('exercise-block-field-item-dropdown', className, status, { filled, active: visible, readonly })}
    >
      <DolPopover
        placement="top-start"
        trigger="click"
        overlayClassName="un-styled no-arrow custom-inner-content"
        visible={!readonly && visible}
        onVisibleChange={(visibleChange) => !readonly && setVisible(visibleChange)}
        content={({ setTooltipOpen }) => (
          <SelectOptionList>
            <ScrollContent>
              {options?.map(({ key, value }) => {
                return (
                  <DropdownItemItemLayout
                    key={key}
                    content={
                      <SelectDropdownItemContent>
                        <SelectDropdownItemContent.Text text={value} />
                      </SelectDropdownItemContent>
                    }
                    onClick={() => {
                      setTooltipOpen(false);
                      onChange?.(key);
                    }}
                    selected={key === selectedKey}
                  />
                );
              })}
            </ScrollContent>
          </SelectOptionList>
        )}
        disabled={mode !== 'popover'}
      >
        <Dropdown
          className="dropdown-action"
          onClick={() => mode === 'action' && onClick?.()}
        >
          <DropdownLayout>
            <ResponsiveTypography.Text className="dropdown-text" variant="medium/16-24">
              {selectedOption?.value || ''}
            </ResponsiveTypography.Text>
            <IconSystemV2ChevronDropdownDown className="dropdown-icon" size={24} ml="4px" />
          </DropdownLayout>
        </Dropdown>
      </DolPopover>
      <div className="text-line" />
    </Container>
  );
};
